import { useEffect, useRef } from "react";
import moment from 'moment-timezone';
import useClientConfig from "../hooks/clientConfigHook";
import { FloatingLabel } from "./FloatingLabel";
import PropTypes from 'prop-types';
import { cdate, now } from "../utils/CDate";

export const customCtrlDef = {
    toString: ({ def, value, clientConfig }) => {
        let currentTime = now(clientConfig.tz);
        if (value) {
            let elapsedTime = cdate(value, clientConfig.tz).duration(currentTime);
            // TODO this may need to be formatted differently for different locales
            return `${('00' + elapsedTime.hours()).slice(-2)}:${('00' + elapsedTime.minutes()).slice(-2)}:${('00' + elapsedTime.seconds()).slice(-2)}${elapsedTime.days() > 0 ? '+' + elapsedTime.days() : ''}`;
        } else {
            return def.showDate ? currentTime.dateTimeWithSecondsFormat(def.showZone) : currentTime.timeWithSecondsFormat(def.showZone)
        }
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                [
                    { type: "checkbox", field: "showDate", label: t('showDate') },
                    { type: "checkbox", field: "showZone", label: t('showZone') },
                    { type: "text", field: "defaultTZ", label: t('timezone') }
                ],
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, value, ...props }) => <Clock baseDate={value} showDate={def.showDate} showZone={def.showZone} defaultTZ={def.defaultTZ} {...props}></Clock>
}

const intervalFunctions = [];
setInterval(() => {
    intervalFunctions.forEach(f => f());
}, 1000);

/**
 * A clock showing either the current time or the time since a given base time.
 * @component
 */
const Clock = ({ baseDate, skew, defaultTZ, placeholder, hideLabel = false, showDate = false, showZone = false}) => {
    const clientConfig = useClientConfig();
    const tz = defaultTZ ?? clientConfig.tz;

    const clockDiv = useRef();

    useEffect(() => {
        const updateClock = () => { 
            let currentTime;
            let str = '';
            if (clientConfig.tz) {
                currentTime = moment().tz(tz).toDate();
            } else {
                currentTime = moment().toDate();
            }
            if (baseDate) {
                let base = moment.tz(baseDate, tz).add(skew ?? 0, 's').toDate();//new Date(attrs.baseDate);
                let diff = Math.abs(currentTime.getTime() - base.getTime());
                let hours = Math.floor(diff / (1000 * 60 * 60));
                let days = Math.floor(hours / 24);
                let displayHours = hours % 24;
                let mins = Math.floor((diff / (1000 * 60)) - (hours * 60));
                let secs = Math.floor((diff / (1000)) - (hours * 60 * 60) - (mins * 60));
                str = (
                    (displayHours < 10 ? ('0' + displayHours.toString()) : displayHours.toString()) + ':' +
                    (mins < 10 ? ('0' + mins.toString()) : mins.toString()) + ':' +
                    (secs < 10 ? ('0' + secs.toString()) : secs.toString()) +
                    (days ? ('&nbsp;' + (days > 0 ? '+' : '') + days.toString()) : '')
                );
            } else {
                if (showDate === true) {
                    str = moment.tz(currentTime, tz).format('ddd DD&#8209;MMM&#8209;YY') + '  ';
                }
                str += moment.tz(currentTime, tz).format('HH:mm:ss');
            }
            if (showZone && clientConfig.timezones?.enable) {
                str += '&nbsp;&nbsp;' + moment.tz(currentTime, tz).format('z');
            }
            if (clockDiv.current) {
                clockDiv.current.innerHTML = str;
            }
        }
        intervalFunctions.push(updateClock);
        return () => {
            let idx = intervalFunctions.indexOf(updateClock);
            intervalFunctions.splice(idx, 1);
        };
    }, [clientConfig, baseDate, showDate, showZone, skew, tz])
    return <FloatingLabel placeholder={placeholder ?? ' '} value={true} hideLabel={hideLabel}><span ref={clockDiv}></span></FloatingLabel>;
}

Clock.propTypes = {
    /**
     * Controls if the clock shows the date in addition to the time
     */
    showDate: PropTypes.bool,

    /**
     * Controls if the timezone is shown in addition to the time
     */
    showZone: PropTypes.bool,

    /**
     * Used to specify a base date from which a difference counter is run
     */
    baseDate: PropTypes.string,

    /**
     * Offset between the local clock and the server clock to adjust clock display
     */
    skew: PropTypes.number,

    /**
     * The label for the clock
     */
    placeholder: PropTypes.string,

    /**
     * Timezone to use when rendering the time
     */
    tz: PropTypes.string,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool
}

export default Clock;